import { useCallback, useMemo } from 'react';

import { useNavigate, useOutletContext } from 'react-router-dom';

import { DataTableFiltersV2 } from '@/components/data-table/filters-v2';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { TableIds } from '@/lib/constants/table-ids';
import { SettingsPageWrapper } from '@/modules/settings';
import { AuthenticatedRoutes } from '@/router/routes';

import { InviteFailuresDialog } from '../../components/member-access/invite-failures-dialog';
import { MemberAccessTable } from '../../components/member-access/member-access-table';
import { useMemberAccessFooter } from '../../hooks/member-access/use-member-access-footer';
import { useMemberAccessPage } from '../../hooks/member-access/use-member-access-page';
import { SettingsOutletContext } from '../../types/settings-footer';

export const MembersPage = () => {
  const navigate = useNavigate();
  const { setFooterState } = useOutletContext<SettingsOutletContext>();

  const {
    t,
    filtersConfig,
    filterState,
    table,
    saveColumnSizing,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    errorMessage,
    totalMembers,
    isLoadingGroups,
    isInviteMode,
    enterInviteMode,
    exitInviteMode,
    inviteFailures,
    clearInviteFailures,
    changesCount,
    selectedCount,
    isSavePending,
    isInvitePending,
    handleSave,
    handleInviteAndSave,
    resetAllEdits,
    unsavedChangesGuard,
  } = useMemberAccessPage();

  useMemberAccessFooter({
    changesCount,
    selectedCount,
    isInviteMode,
    isLoading,
    error,
    isPending: isSavePending,
    isInvitePending,
    onSave: handleSave,
    onInviteAndSave: handleInviteAndSave,
    onCancelInviteMode: exitInviteMode,
    onReset: resetAllEdits,
    setFooterState,
    t,
  });

  const handleModalCancel = useCallback(() => {
    unsavedChangesGuard.handleModalCancel();
  }, [unsavedChangesGuard]);

  const unsavedChangesGuardProps = useMemo(
    () => ({
      isModalOpen: unsavedChangesGuard.isModalOpen,
      onDiscard: unsavedChangesGuard.handleModalDiscard,
      onCancel: handleModalCancel,
    }),
    [unsavedChangesGuard.isModalOpen, unsavedChangesGuard.handleModalDiscard, handleModalCancel]
  );

  return (
    <SettingsPageWrapper
      isLoading={isLoadingGroups}
      error={error}
      unsavedChangesGuard={unsavedChangesGuardProps}
    >
      <div className="absolute inset-0 flex flex-col overflow-hidden py-6">
        <Card
          className="flex min-h-0 w-full flex-1 flex-col overflow-hidden p-6"
          style={{ maxWidth: '1000px' }}
        >
          {/* Card Header - Fixed */}
          <div className="mb-6 shrink-0">
            {/* Title and action buttons */}
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold leading-7">{t('pages.members.title')}</h2>
              <div className="flex items-center gap-2">
                {!isInviteMode && (
                  <Button variant="outline" size="sm" onClick={enterInviteMode}>
                    {t('pages.members.invite')}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(AuthenticatedRoutes.Team)}
                >
                  {t('pages.members.manageTeamMembers')}
                </Button>
              </div>
            </div>

            {/* Filters — apply immediately and non-destructively */}
            <div className="mb-4">
              <DataTableFiltersV2
                filtersConfig={filtersConfig}
                filterState={filterState}
                tableId={TableIds.MEMBER_ACCESS}
              />
            </div>

            {/* Members counter */}
            <div className="text-sm text-muted-foreground">
              {t('pages.members.membersCount', { count: totalMembers })}
            </div>
          </div>

          {/* Table with Scrollable Body */}
          <div className="flex min-h-0 flex-1 flex-col">
            <MemberAccessTable
              table={table}
              isLoading={isLoading || isFetchingNextPage}
              hasMore={hasNextPage}
              onLoadMore={() => fetchNextPage()}
              error={errorMessage}
              stickyHeader
              fixedHeight
              tableId={TableIds.MEMBER_ACCESS}
              onResizeEnd={saveColumnSizing}
            />
          </div>
        </Card>
      </div>
      <InviteFailuresDialog failures={inviteFailures} onClose={clearInviteFailures} />
    </SettingsPageWrapper>
  );
};
